import { useEffect, useRef } from 'react';

export function tgWebApp() {
  return window.Telegram?.WebApp;
}

export type ClientInvoiceStatus = TelegramInvoiceStatus;

export function supportsTelegramInvoice(): boolean {
  return typeof tgWebApp()?.openInvoice === 'function';
}

export function openTelegramInvoice(url: string): Promise<ClientInvoiceStatus> {
  const webApp = tgWebApp();
  if (!webApp?.openInvoice) {
    return Promise.reject(new Error('telegram_open_invoice_unavailable'));
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (status: ClientInvoiceStatus) => {
      if (settled) return;
      settled = true;
      resolve(status);
    };

    try {
      webApp.openInvoice?.(url, (status) => finish(status));
    } catch {
      if (settled) return;
      settled = true;
      reject(new Error('telegram_open_invoice_failed'));
    }
  });
}

export function openExternalLink(url: string) {
  const webApp = tgWebApp();
  if (webApp?.openLink) {
    webApp.openLink(url);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
}

export function hapticImpact(style: 'light' | 'medium' | 'heavy' = 'medium') {
  try {
    tgWebApp()?.HapticFeedback?.impactOccurred(style);
  } catch {
    // no-op вне Telegram
  }
}

export function hapticNotify(type: 'success' | 'warning' | 'error') {
  try {
    tgWebApp()?.HapticFeedback?.notificationOccurred(type);
  } catch {
    // no-op
  }
}

function hapticSelection() {
  try {
    tgWebApp()?.HapticFeedback?.selectionChanged?.();
  } catch {
    // no-op
  }
}

/** Безопасные вызовы Haptic Feedback (браузер без Telegram — no-op). */
export const haptic = {
  success: () => hapticNotify('success'),
  error: () => hapticNotify('error'),
  warning: () => hapticNotify('warning'),
  selection: () => hapticSelection(),
  impact: (style: 'light' | 'medium' | 'heavy') => hapticImpact(style),
};

export function getInitDataRaw(): string {
  const initData = tgWebApp()?.initData ?? '';
  console.log('[telegram] initData length:', initData.length);
  console.log('[telegram] Telegram.WebApp available:', !!tgWebApp());
  return initData;
}

/**
 * Telegram BackButton: показать и подписаться; при размонтировании — hide + offClick.
 */
export function useBackButton(onBack: () => void, enabled = true) {
  const ref = useRef(onBack);
  ref.current = onBack;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const bb = tgWebApp()?.BackButton;
    if (!bb?.show || !bb.onClick) return;

    const handler = () => ref.current();
    bb.show();
    bb.onClick(handler);
    return () => {
      try {
        bb.offClick?.(handler);
      } finally {
        bb.hide?.();
      }
    };
  }, [enabled]);
}

export interface UseMainButtonOptions {
  text: string;
  onClick: () => void;
  /** Если false — MainButton скрыт, обработчик снимается. */
  visible: boolean;
  disabled?: boolean;
  loading?: boolean;
}

/**
 * Telegram MainButton: текст, видимость, disabled/loading.
 * При loading текст меняется на нейтральный «Подождите…».
 */
export function useMainButton(opts: UseMainButtonOptions) {
  const {
    text,
    visible,
    disabled = false,
    loading = false,
  } = opts;

  const clickRef = useRef(opts.onClick);
  clickRef.current = opts.onClick;

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mb = tgWebApp()?.MainButton;
    if (!mb?.setText || !mb.onClick) return;

    const handler = () => clickRef.current();
    const label = loading ? 'Подождите…' : text;
    mb.setText(label);

    if (!visible) {
      mb.offClick?.(handler);
      mb.hide?.();
      return () => {
        mb.offClick?.(handler);
        mb.hide?.();
      };
    }

    mb.onClick(handler);
    mb.show?.();

    if (disabled || loading) mb.disable?.();
    else mb.enable?.();

    return () => {
      mb.offClick?.(handler);
      mb.hide?.();
    };
  }, [text, visible, disabled, loading]);
}
